import * as THREE from 'three';
import { REGIONS } from '../pharmacology/regions';
import type { RegionId } from '../pharmacology/types';
import { lerpColor } from './materials';
import { resolveAnchor } from './loadCortexMesh';

const DIM_COLOR = 0x33507a;
const DIM_OPACITY = 0.12;
const ACTIVE_OPACITY_RANGE = 0.75;

/**
 * The 11 anatomical region-node spheres. Brightness/color at each node is
 * driven externally (from computeDoseResponse output), never hand-tuned per
 * substance — this file only owns rendering the nodes themselves.
 */
export class RegionNodes {
  readonly group: THREE.Group;
  private readonly nodes: Map<RegionId, THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>>;

  constructor() {
    this.group = new THREE.Group();
    this.nodes = new Map();

    for (const region of Object.values(REGIONS)) {
      const geometry = new THREE.SphereGeometry(0.07, 16, 16);
      const material = new THREE.MeshBasicMaterial({
        color: DIM_COLOR,
        transparent: true,
        opacity: DIM_OPACITY,
        // These are deep structures sitting inside the opaque cortical shell.
        // Skipping the depth test makes them readable as an atlas overlay
        // rather than hiding them inside the cortex.
        depthTest: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(...resolveAnchor(region.anchor));
      this.group.add(mesh);
      this.nodes.set(region.id, mesh);
    }
  }

  /**
   * Sets each node's brightness from a 0-1 activity map (regions absent from
   * the map, or the whole map being undefined, are treated as inactive/dim).
   * activeColor tints nodes toward the active substance's palette color.
   */
  setActivity(regionActivity: Partial<Record<RegionId, number>> | undefined, activeColor: THREE.ColorRepresentation): void {
    for (const [regionId, mesh] of this.nodes) {
      const activity = regionActivity?.[regionId] ?? 0;
      mesh.material.opacity = DIM_OPACITY + activity * ACTIVE_OPACITY_RANGE;
      mesh.material.color.copy(lerpColor(DIM_COLOR, activeColor, activity));
    }
  }

  getNode(regionId: RegionId): THREE.Mesh | undefined {
    return this.nodes.get(regionId);
  }
}
